import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import apiClient from '../../../services/apiClient'
import {
  DrawioBlockValue,
  createDiagramId,
} from './drawioHtml'
import './DrawioEmbedModal.css'

const EMBED_ORIGIN = 'https://embed.diagrams.net'
const EMBED_URL =
  `${EMBED_ORIGIN}/?embed=1&ui=atlas&spin=1&proto=json&libraries=1` +
  `&saveAndExit=1&noSaveBtn=0&noExitBtn=0&keepmodified=1`

export type DrawioSaveResult = DrawioBlockValue & {
  xml: string
}

export type DrawioEmbedHandle = {
  /** Export + upload + onSaved; if exitAfter, calls onClose */
  save: (exitAfter?: boolean) => Promise<void>
  isDirty: () => boolean
}

type Props = {
  open: boolean
  onClose: () => void
  /**
   * When set, Đóng / Exit hỏi parent trước (Lưu / Không lưu).
   * Parent should call save() or onClose() after decision.
   */
  onRequestClose?: () => void
  /** Existing diagram to edit; omit for blank canvas */
  initial?: Partial<DrawioBlockValue> & { xml?: string }
  defaultTitle?: string
  onSaved: (result: DrawioSaveResult) => void | Promise<void>
  /** Upload endpoint (default student homework diagram) */
  uploadPath?: string
  /** Proxy path for loading xmlUrl (default student) */
  xmlProxyPath?: string
  /** absolute inset-0 for fullscreen container (not body-fixed) */
  contained?: boolean
  /** Notify parent when canvas has edits */
  onDirtyChange?: (dirty: boolean) => void
  /** Hide toast on save (parent shows own message) */
  quietSave?: boolean
  titleLabel?: string
}

function dataUrlToBlob(dataUrl: string): Blob {
  const parts = dataUrl.split(',')
  const header = parts[0] || ''
  const body = parts[1] || ''
  const mimeMatch = /data:([^;]+)/.exec(header)
  const mime = mimeMatch?.[1] || 'image/png'
  const binary = atob(body)
  const len = binary.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

async function loadXmlFromUrl(
  xmlUrl: string,
  xmlProxyPath: string
): Promise<string> {
  if (!xmlUrl) return ''
  try {
    const res = await apiClient.get(xmlProxyPath, {
      params: { url: xmlUrl },
      responseType: 'text',
      transformResponse: [(d) => d],
      timeout: 30000,
    })
    if (typeof res.data === 'string' && res.data.trim()) return res.data
  } catch {
    /* try direct fetch */
  }
  try {
    const res = await fetch(xmlUrl)
    if (res.ok) return await res.text()
  } catch {
    /* ignore */
  }
  return ''
}

async function uploadDiagramAssets(params: {
  diagramId: string
  xml: string
  pngDataUrl: string
  uploadPath: string
}): Promise<{ pngUrl: string; xmlUrl: string; diagramId: string }> {
  const pngBlob = dataUrlToBlob(params.pngDataUrl)
  const xmlBlob = new Blob([params.xml], { type: 'application/xml' })
  const form = new FormData()
  form.append('diagramId', params.diagramId)
  form.append('png', pngBlob, `${params.diagramId}.png`)
  form.append('xml', xmlBlob, `${params.diagramId}.drawio`)

  const res = await apiClient.post(params.uploadPath, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  const data = res.data?.data
  if (!data?.pngUrl || !data?.xmlUrl) {
    throw new Error(res.data?.message || 'Upload diagram thất bại')
  }
  return {
    diagramId: data.diagramId || params.diagramId,
    pngUrl: data.pngUrl,
    xmlUrl: data.xmlUrl,
  }
}

const DrawioEmbedModal = forwardRef<DrawioEmbedHandle, Props>(function DrawioEmbedModal(
  {
    open,
    onClose,
    onRequestClose,
    initial,
    defaultTitle = 'Sơ đồ quy trình',
    onSaved,
    uploadPath = '/student/uploads/diagram',
    xmlProxyPath = '/student/uploads/diagram-xml',
    contained = false,
    onDirtyChange,
    quietSave = false,
    titleLabel = 'Tên quy trình',
  },
  ref
) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [ready, setReady] = useState(false)
  const [loadingXml, setLoadingXml] = useState(false)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState(defaultTitle)
  const pendingXmlRef = useRef<string | null>(null)
  const latestXmlRef = useRef<string>('')
  const dirtyRef = useRef(false)
  const diagramIdRef = useRef(initial?.diagramId || createDiagramId())
  const versionRef = useRef(Number(initial?.version) || 1)
  const exportWaitRef = useRef<{
    xml: string
    resolve: (png: string) => void
    reject: (e: Error) => void
  } | null>(null)

  const setDirty = useCallback(
    (v: boolean) => {
      dirtyRef.current = v
      onDirtyChange?.(v)
    },
    [onDirtyChange]
  )

  const requestClose = useCallback(() => {
    if (onRequestClose) onRequestClose()
    else onClose()
  }, [onClose, onRequestClose])

  useEffect(() => {
    if (!open || contained) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open, contained])

  useEffect(() => {
    if (!open) return
    diagramIdRef.current = initial?.diagramId || createDiagramId()
    versionRef.current = Number(initial?.version) || 1
    setTitle(initial?.title?.trim() || defaultTitle)
    setReady(false)
    setSaving(false)
    setLoadingXml(true)
    pendingXmlRef.current = null
    latestXmlRef.current = ''
    setDirty(false)

    let cancelled = false
    ;(async () => {
      let xml = initial?.xml || ''
      if (!xml && initial?.xmlUrl) {
        xml = await loadXmlFromUrl(initial.xmlUrl, xmlProxyPath)
      }
      if (cancelled) return
      pendingXmlRef.current = xml || null
      latestXmlRef.current = xml || ''
      setLoadingXml(false)
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          JSON.stringify({ action: 'load', xml: xml || '', autosave: 1 }),
          EMBED_ORIGIN
        )
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- full cycle per modal open
  }, [open, initial?.diagramId, initial?.xmlUrl, initial?.xml, defaultTitle, xmlProxyPath])

  const postToEditor = useCallback((msg: Record<string, unknown>) => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    win.postMessage(JSON.stringify(msg), EMBED_ORIGIN)
  }, [])

  const requestPngExport = useCallback(
    (xml: string) =>
      new Promise<string>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          if (exportWaitRef.current) {
            exportWaitRef.current.reject(new Error('Export PNG timeout'))
            exportWaitRef.current = null
          }
        }, 45000)
        exportWaitRef.current = {
          xml,
          resolve: (png) => {
            window.clearTimeout(timer)
            resolve(png)
          },
          reject: (e) => {
            window.clearTimeout(timer)
            reject(e)
          },
        }
        postToEditor({
          action: 'export',
          format: 'png',
          xml,
          spinKey: 'export',
          border: 10,
          background: '#ffffff',
        })
      }),
    [postToEditor]
  )

  const handleSaveXml = useCallback(
    async (xml: string, exitAfter: boolean) => {
      if (!xml?.trim()) {
        toast.error('Bảng trắng trống — hãy vẽ trước khi lưu')
        return
      }
      try {
        setSaving(true)
        const pngDataUrl = await requestPngExport(xml)
        const uploaded = await uploadDiagramAssets({
          diagramId: diagramIdRef.current,
          xml,
          pngDataUrl,
          uploadPath,
        })
        diagramIdRef.current = uploaded.diagramId
        const nextVersion = initial?.diagramId
          ? (versionRef.current || 1) + 1
          : 1
        versionRef.current = nextVersion
        await onSaved({
          diagramId: uploaded.diagramId,
          pngUrl: uploaded.pngUrl,
          xmlUrl: uploaded.xmlUrl,
          title: title.trim() || defaultTitle,
          version: versionRef.current,
          xml,
        })
        setDirty(false)
        if (!quietSave) toast.success('Đã upload sơ đồ — nhớ lưu bài nếu chưa tự lưu')
        if (exitAfter) onClose()
      } catch (e: any) {
        toast.error(e?.message || 'Lưu thất bại')
        throw e
      } finally {
        setSaving(false)
      }
    },
    [
      defaultTitle,
      initial?.diagramId,
      onClose,
      onSaved,
      quietSave,
      requestPngExport,
      setDirty,
      title,
      uploadPath,
    ]
  )

  useImperativeHandle(
    ref,
    () => ({
      save: async (exitAfter = true) => {
        const xml = latestXmlRef.current?.trim()
        if (!xml) {
          // ask editor for current xml via export path – empty canvas
          toast.error('Bảng trắng trống — hãy vẽ trước khi lưu')
          throw new Error('empty')
        }
        await handleSaveXml(xml, exitAfter)
      },
      isDirty: () => dirtyRef.current,
    }),
    [handleSaveXml]
  )

  useEffect(() => {
    if (!open) return

    const onMessage = (evt: MessageEvent) => {
      if (evt.origin !== EMBED_ORIGIN) return
      let msg: any
      try {
        msg = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data
      } catch {
        return
      }
      if (!msg || typeof msg !== 'object') return

      if (msg.event === 'init') {
        setReady(true)
        const xml = pendingXmlRef.current
        postToEditor({
          action: 'load',
          xml: xml || '',
          autosave: 1,
        })
        return
      }

      if (msg.event === 'autosave') {
        const xml = String(msg.xml || msg.data || '')
        if (xml) {
          latestXmlRef.current = xml
          setDirty(true)
        }
        return
      }

      if (msg.event === 'export') {
        const waiting = exportWaitRef.current
        if (!waiting) return
        const data = String(msg.data || '')
        if (!data) {
          waiting.reject(new Error('Export PNG rỗng'))
          exportWaitRef.current = null
          return
        }
        const png = data.startsWith('data:')
          ? data
          : `data:image/png;base64,${data}`
        waiting.resolve(png)
        exportWaitRef.current = null
        return
      }

      if (msg.event === 'save') {
        const xml = String(msg.xml || msg.data || '')
        if (xml) latestXmlRef.current = xml
        void handleSaveXml(xml, Boolean(msg.exit))
        return
      }

      if (msg.event === 'exit') {
        if (!saving) requestClose()
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [open, handleSaveXml, postToEditor, requestClose, saving, setDirty])

  if (!open) return null

  const rootClass = contained
    ? 'hw-drawio-modal hw-drawio-modal--contained'
    : 'hw-drawio-modal'

  return (
    <div className={rootClass} role="dialog" aria-modal="true" aria-label="Bảng trắng">
      <div className="hw-drawio-modal__bar">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 min-w-0">
          <span className="text-sm font-semibold text-slate-800 shrink-0">Bảng trắng</span>
          <label className="flex items-center gap-2 text-sm text-slate-600 min-w-0 flex-1">
            <span className="shrink-0">{titleLabel}</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border border-slate-300 rounded-md px-2 py-1 text-sm w-full max-w-md"
              placeholder="VD: Minh họa luồng đăng ký"
              disabled={saving}
            />
          </label>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(loadingXml || saving) && (
            <span className="text-xs text-slate-500">
              {saving ? 'Đang lưu…' : 'Đang tải…'}
            </span>
          )}
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            onClick={requestClose}
            disabled={saving}
          >
            Đóng
          </button>
        </div>
      </div>
      <div className="hw-drawio-modal__frame-wrap">
        <iframe
          ref={iframeRef}
          title="draw.io editor"
          className="hw-drawio-modal__iframe"
          src={EMBED_URL}
          allow="clipboard-read; clipboard-write"
        />
        {!ready && (
          <div className="hw-drawio-modal__loading">Đang mở bảng trắng…</div>
        )}
      </div>
      <p className="hw-drawio-modal__hint">
        Vẽ xong bấm <strong>Save</strong> trong draw.io hoặc <strong>Đóng</strong> —
        nếu còn thay đổi chưa lưu, hệ thống sẽ hỏi Lưu / Không lưu.
      </p>
    </div>
  )
})

export default DrawioEmbedModal
